import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Textarea from 'react-textarea-autosize';
import PaperClipIcon from '@heroicons/react/24/solid/PaperClipIcon';
import Button from '@medflex/components/stories/Button';
import InputCheckbox from '@medflex/components/stories/InputCheckbox';
import Badge, { KINDS } from '@medflex/components/stories/Badge';
import InputField from '@medflex/components/stories/InputField';
import Modal from '@medflex/components/stories/Modal';
import { FileInput, FileItem } from '@medflex/components/stories/FileUpload';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import ChevronLeftIcon from '@heroicons/react/24/solid/ChevronLeftIcon';
import { constant } from '@medflex/commons';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import useUppy from '@medflex/hooks/useUppy';
import useUserSession from '@medflex/hooks/useUserSession';
import { trackEvent, trackCustomEvent } from '@medflex/utils/Tracking';
import config from '@medflex/utils/config';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';

import useIsModal from '../hooks/useIsModal';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';
import useModal from '../hooks/useModal';
import useSnippets from '../hooks/useSnippets';
import useFeatures from '../hooks/useFeatures';
import useUserProfile from '../hooks/useUserProfile';

const TextSnippetsEditModal = ({ onGoBackClick, onClose }) => {
  const { close, open } = useModal();
  const { t } = useTranslation('physician');
  const { snippets, create, update } = useSnippets(true);
  const titleRef = useRef();
  const isWideScreen = useIsWideScreen();
  const [isLoading, setIsLoading] = useState(false);
  const { isModal } = useIsModal();
  const { id } = useLocationSearchParams();
  const { session } = useUserSession();
  const dropTarget = useRef();
  const [currentSnippet, setCurrentSnippet] = useState();
  const [pamFeature] = useFeatures([FEATURE_KEYS.PAM]);
  const { userTags } = useUserProfile();

  const {
    control,
    formState,
    handleSubmit: wrapSubmit,
    register: registerFormElement,
    getValues,
    setValue,
    trigger,
    watch,
  } = useForm({ mode: 'onChange' });

  const text = watch('text');
  const tags = watch('tags');
  const quickReply = watch('quickReply');

  const handleError = (_, e) => {
    showAlertToast(t('toasts.textSnippetFileUpload.tooManyFiles.title'), e.message);
  };

  const {
    errors: fileErrors,
    files,
    removeFile,
    register: registerFileInput,
    retry,
    setOptions,
    upload,
    uploading,
  } = useUppy({
    dropTarget,
    multiple: true,
    restrictions: {
      maxTotalFileSize: 50 * 1024 * 1024,
    },
    endpoint: `${config.INSTANT_MESSAGE_SERVICE_ENDPOINT}/text-snippets/uploads`,
    headers: {
      AUTHORIZATION: `Bearer ${session.access_token || ''}`,
    },
    onRestrictionFailed: handleError,
    localeOverrides: {
      youCanOnlyUploadX: t('toasts.textSnippetFileUpload.tooManyFiles.text'),
      exceedsSize: t('toasts.textSnippetFileUpload.filesTooLarge.text'),
    },
  });

  useEffect(() => {
    if (id) {
      setCurrentSnippet(snippets.find(({ id: snippetId }) => id === snippetId));
    }
  }, [snippets, setCurrentSnippet, id]);

  useEffect(() => {
    if (currentSnippet && setOptions) {
      const { files = [] } = currentSnippet;

      setOptions({
        restrictions: {
          maxNumberOfFiles: 5 - files.length,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSnippet]);

  const isBelowLimit = snippets.length < constant.snippets.maxSnippets;

  useEffect(() => {
    if (titleRef.current) {
      registerFormElement(titleRef.current, {
        required: true,
        maxLength: constant.snippets.maxTitle,
        validate: value => !!value.trim(),
      });
      if (isModal && isWideScreen) {
        titleRef.current.focus();
      }
    }
  }, [titleRef, isModal, isWideScreen, registerFormElement]);

  useEffect(() => {
    if (id && currentSnippet) {
      setValue('title', currentSnippet.title);
      setValue('text', currentSnippet.text);
      setValue('tags', currentSnippet.tags || []);
      setValue('quickReply', currentSnippet.quickReply);
      trigger();
    }
  }, [currentSnippet, id, setValue, trigger]);

  const isSubmitEnabled = useMemo(() => {
    const { isValid } = formState;

    if (
      isValid &&
      (isBelowLimit || id) &&
      (text || files.length || currentSnippet?.files?.length) &&
      (!quickReply || tags?.length)
    )
      return true;
    return false;
  }, [text, formState, isBelowLimit, id, files, currentSnippet, quickReply, tags?.length]);

  const fileUploadCompleted = useMemo(() => {
    if (!uploading && files.length > 0) {
      if (!files.some(({ progress }) => !progress.uploadComplete)) {
        return true;
      }
    }
    return false;
  }, [files, uploading]);

  const retryFailedFiles = useCallback(
    () => Promise.all(fileErrors.map(error => retry(error.file))),
    [fileErrors, retry],
  );

  const getFileIdFromUri = uri => uri.split('/').pop();

  const handleSubmit = useCallback(
    async (data, event) => {
      setIsLoading(true);
      const { title, text, tags = [] } = data;

      if (files.length > 0 && !fileUploadCompleted) {
        if (fileErrors.length > 0) {
          retryFailedFiles();
          return;
        }
        upload();
        return;
      }

      if (id) {
        try {
          await update({
            payload: {
              id,
              title,
              ...(pamFeature.active
                ? { tags: quickReply ? tags : [], quickReply }
                : { tags: [], quickReply: false }),
              ...(text && { text }),
              ...((files.length || currentSnippet.files?.length) && {
                files: [
                  ...(currentSnippet.files ? currentSnippet.files : []),
                  ...files.map(({ uploadURL, name, type, size }) => ({
                    id: getFileIdFromUri(uploadURL),
                    name,
                    detectedMediaType: type,
                    mediaType: type,
                    bytes: size,
                  })),
                ],
              }),
            },
          });
          showSuccessToast(
            t('toasts.snippets_adjust.success.title'),
            <span>
              <button
                type="button"
                className="text-blue-500"
                onClick={() => open({ modal: 'textSnippetsEdit' })}
              >
                {t('toasts.snippets_adjust.success.text')}
              </button>
            </span>,
            {
              toastId: 'snippetEditSuccess',
            },
          );
          trackEvent(event, { actionText: 'manage_Textblock' });
          if (files.length) {
            trackCustomEvent({
              eventCategory: 'user interactions',
              eventAction: 'TextSnippetsEditModal | form',
              eventName: 'manage_Textblock_attachment_attached',
            });
          }
        } catch {
          showAlertToast(
            t('toasts.snippets_adjust.failure.title'),
            t('toasts.snippets_adjust.failure.text'),
            {
              toastId: 'snippetEditFailed',
            },
          );
        }
      } else {
        try {
          await create({
            payload: {
              title,
              ...(pamFeature.active
                ? { tags: quickReply ? tags : [], quickReply }
                : { tags: [], quickReply: false }),
              ...(text && { text }),
              ...(files.length && {
                files: files.map(({ uploadURL }) => getFileIdFromUri(uploadURL)),
              }),
            },
          });
          showSuccessToast(
            t('toasts.snippets_new.success.title'),
            <span>
              <button
                type="button"
                className="text-blue-500"
                onClick={() => open({ modal: 'textSnippetsEdit' })}
              >
                {t('toasts.snippets_new.success.text')}
              </button>
            </span>,
            {
              toastId: 'snippetAddSuccess',
            },
          );
          trackEvent(event, { actionText: 'create_Textblock' });
          if (snippets.length === 9) {
            trackEvent(event, { actionText: 'create_Textblock_nr_10' });
          }
          if (files.length) {
            trackCustomEvent({
              eventCategory: 'user interactions',
              eventAction: 'TextSnippetsEditModal | form',
              eventName: 'manage_Textblock_attachment_attached',
            });
          }
        } catch (e) {
          showAlertToast(
            t('toasts.snippets_new.failure.title'),
            t('toasts.snippets_new.failure.text'),
            {
              toastId: 'snippetAddFailed',
            },
          );
        }
      }

      close({ modal: 'textSnippetsEdit', id });
      onClose({ title, text });
    },
    [
      close,
      create,
      currentSnippet,
      fileErrors.length,
      fileUploadCompleted,
      files,
      id,
      onClose,
      open,
      retryFailedFiles,
      snippets.length,
      t,
      update,
      upload,
      pamFeature,
      quickReply,
    ],
  );

  useEffect(() => {
    if (fileUploadCompleted) handleSubmit(getValues(), new CustomEvent('submit'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadCompleted]);

  const title = useMemo(() => {
    if (id) return t('modals.snippets.creation.title_adjust');
    return t('modals.snippets.creation.title_new');
  }, [id, t]);

  const handleOnClose = () => {
    close({ modal: 'textSnippetsEdit', id });
    onClose();
  };

  const handleGoBack = () => {
    close({ modal: 'textSnippetsEdit', id });
    onGoBackClick();
  };

  return (
    <Modal title={title} showCloseButton onClickCloseButton={handleOnClose} dropTarget={dropTarget}>
      <div data-component="TextSnippetsEditModal">
        <Button kind="link" onClick={handleGoBack} className="text-slate-600 hover:no-underline">
          <ChevronLeftIcon className="h-4" />
          <span>{t('modals.snippets.creation.back_button')}</span>
        </Button>
        {!isBelowLimit && !id && (
          <div className="mt-4">
            <span className="text-red-600">
              {t('modals.snippets.creation.info_limit', {
                count: snippets.length,
                limit: constant.snippets.maxSnippets,
              })}
            </span>
          </div>
        )}
        <FileInput {...registerFileInput()}>
          {({ select }) => (
            <form onSubmit={wrapSubmit(handleSubmit)} className="flex flex-1 flex-col">
              <div className="space-y-4 mb-6">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="full-width flex flex-1 flex-col">
                  <span className="mb-1 font-semibold">
                    {t('modals.snippets.creation.name_input_label')}
                  </span>
                  <InputField
                    ref={titleRef}
                    defaultValue=""
                    name="title"
                    disabled={isLoading || (!isBelowLimit && !id)}
                    maxLength="200"
                  />
                </label>

                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="full-width flex flex-1 flex-col mb-4">
                  <span className="mb-1 font-semibold">
                    {t('modals.snippets.creation.content_input_label')}
                  </span>

                  <Controller
                    control={control}
                    defaultValue=""
                    name="text"
                    render={({ onChange, value, name }) => (
                      <div className="border border-solid border-slate-500 rounded flex flex-grow flex-col">
                        <Textarea
                          className="resize-none self-stretch rounded p-3 leading-tight placeholder-slate-500 outline-none focus:border-blue-500"
                          id={name}
                          maxRows={10}
                          minRows={3}
                          name={name}
                          onChange={val => onChange(val)}
                          readOnly={isLoading || (!isBelowLimit && !id)}
                          maxLength={constant.snippets.maxText}
                          value={value}
                        />
                        {files?.map(file => (
                          <FileItem
                            key={file.id}
                            type={file.type}
                            name={file.name}
                            size={file.size}
                            extension={file.extension}
                            onDelete={() => removeFile({ id: file.id })}
                            percentage={file.progress && file.progress.percentage}
                            hasError={!!file.error}
                            small
                          />
                        ))}
                        {currentSnippet?.files?.map(file => (
                          <FileItem
                            key={file.id}
                            type={file.mediaType}
                            name={file.name}
                            size={file.size}
                            percentage={file.percentage}
                            extension={file.extension}
                            onDelete={() =>
                              setCurrentSnippet({
                                ...currentSnippet,
                                text,
                                files: [...currentSnippet.files.filter(({ id }) => id !== file.id)],
                              })
                            }
                            hasError={!!file.error}
                            small
                          />
                        ))}
                      </div>
                    )}
                    rules={{ maxLength: constant.snippets.maxText }}
                  />
                </label>
                <Button className="self-start text-slate-700" kind="link" onClick={select}>
                  <PaperClipIcon className="w-5" />
                  <p className="ml-2">{t('modals.snippets.creation.button_attachment')}</p>
                </Button>
              </div>

              {pamFeature.active && (
                <div className="space-y-2">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="space-x-2">
                    <Controller
                      control={control}
                      defaultValue={false}
                      name="quickReply"
                      render={({ onChange, value }) => (
                        <InputCheckbox checked={value} onChange={e => onChange(e.target.checked)} />
                      )}
                    />
                    <span className="font-semibold">{t('tagLabels.pamRequestQuickReply')}</span>
                  </label>
                  <div className="ml-8 space-y-2 max-w-[352px] text-sm" hidden={!quickReply}>
                    <div className="text-slate-600">{t('modals.snippets.tagDescription')}</div>
                    <table className="table border w-full">
                      <tbody>
                        <Controller
                          control={control}
                          defaultValue={[]}
                          name="tags"
                          render={({ onChange, value: values }) =>
                            Object.values(userTags).map(({ value, id }) => (
                              <tr className="border" key={id}>
                                <td className="align-middle px-4 py-2">
                                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                  <label className="flex items-center">
                                    <div className="flex-1 flex">
                                      <Badge kind={KINDS.SECONDARY} className="inline-block">
                                        {value}
                                      </Badge>
                                    </div>
                                    <div className="flex-1 [@media(min-width:420px)]:text-center text-right">
                                      <InputCheckbox
                                        name="tags"
                                        checked={!!values.find(t => t.id === id)}
                                        onChange={e => {
                                          const { checked } = e.target;
                                          const tag = { id, value };
                                          const newTags = checked
                                            ? [...values, tag]
                                            : values.filter(t => t.id !== id);
                                          onChange(newTags);
                                        }}
                                      />
                                    </div>
                                  </label>
                                </td>
                              </tr>
                            ))
                          }
                        />
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <Button
                className="my-6 self-end"
                type="submit"
                isLoading={isLoading}
                isDisabled={!isSubmitEnabled}
              >
                {id
                  ? t('modals.snippets.creation.button_adjust')
                  : t('modals.snippets.creation.button_new')}
              </Button>
            </form>
          )}
        </FileInput>
      </div>
    </Modal>
  );
};

TextSnippetsEditModal.defaultProps = {
  onGoBackClick: () => {},
  onClose: () => {},
};

TextSnippetsEditModal.propTypes = {
  onGoBackClick: PropTypes.func,
  onClose: PropTypes.func,
};

export default TextSnippetsEditModal;
